import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  CreditCard, CheckCircle, AlertCircle, Loader2, 
  ExternalLink, ArrowLeft, Shield, Banknote, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProviderStripeSetup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, token, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [stripeStatus, setStripeStatus] = useState(null);
  const [error, setError] = useState(null);

  const isSuccess = searchParams.get('success') === 'true';
  const isRefresh = searchParams.get('refresh') === 'true';

  useEffect(() => {
    // Wait for auth to finish loading
    if (authLoading) return;
    
    // If not authenticated or not a provider, redirect
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.role !== 'provider') {
      navigate('/dashboard');
      return;
    }
    fetchStripeStatus();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (isSuccess) {
      toast.success('Stripe onboarding completed! Refreshing status...');
      fetchStripeStatus();
    }
    if (isRefresh) {
      toast.info('Please complete your Stripe onboarding');
    }
  }, [isSuccess, isRefresh]);

  const fetchStripeStatus = async () => {
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/providers/stripe/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setStripeStatus(data);
      } else {
        const errorData = await response.json().catch(() => ({}));
        const errorMsg = errorData.detail || 'Failed to fetch Stripe status';
        setError(errorMsg);
        // Don't show toast for "not authenticated" as we'll redirect
        if (response.status !== 401) {
          toast.error(errorMsg);
        }
      }
    } catch (error) {
      setError('Error connecting to server');
      toast.error('Error connecting to server');
    } finally {
      setLoading(false);
    }
  };

  const createStripeAccount = async () => {
    setActionLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/providers/stripe/create-account`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Clone response to avoid "body stream already read" error
      const responseClone = response.clone();
      let data = {};
      
      try {
        data = await responseClone.json();
      } catch (jsonError) {
        // If JSON parsing fails, try reading as text
        try {
          const text = await response.text();
          console.error('Response text:', text);
        } catch (textError) {
          console.error('Could not read response');
        }
      }
      
      if (response.ok) {
        toast.success('Compte Stripe créé ! Complétez maintenant l\'onboarding.');
        await fetchStripeStatus();
        await startOnboarding();
      } else {
        const errorMsg = data.detail || 'Échec de la création du compte Stripe. Stripe Connect doit être activé par l\'administrateur.';
        console.error('Stripe error:', errorMsg);
        setError(errorMsg);
        toast.error(errorMsg, { duration: 10000 });
      }
    } catch (error) {
      console.error('Network error:', error);
      // Provide helpful default message if Stripe Connect is the likely issue
      const msg = 'Stripe Connect n\'est pas activé. L\'administrateur doit l\'activer sur dashboard.stripe.com/connect';
      setError(msg);
      toast.error(msg, { duration: 10000 });
    } finally {
      setActionLoading(false);
    }
  };

  const startOnboarding = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/providers/stripe/onboarding-link`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ origin_url: window.location.origin })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.onboarding_url;
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to start onboarding');
      }
    } catch (error) {
      toast.error('Error starting onboarding');
    } finally {
      setActionLoading(false);
    }
  };

  const openStripeDashboard = async () => {
    setActionLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/providers/stripe/dashboard-link`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        window.open(data.dashboard_url, '_blank');
      } else {
        toast.error('Failed to get dashboard link');
      }
    } catch (error) {
      toast.error('Error opening dashboard');
    } finally {
      setActionLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  const isFullySetup = stripeStatus?.charges_enabled && stripeStatus?.payouts_enabled;
  const needsOnboarding = stripeStatus?.has_account && !stripeStatus?.onboarding_complete;

  return (
    <div className="min-h-screen bg-background pt-24 pb-8" data-testid="provider-stripe-setup-page">
      <div className="max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={() => navigate('/provider/profile')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
              <CreditCard className="w-8 h-8 text-accent" />
              {t('admin.stripe.setup.title')}
            </h1>
            <p className="text-muted-foreground mt-1">
              {t('admin.stripe.setup.subtitle')}
            </p>
          </div>
        </div>

        {/* Status Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                {t('admin.stripe.setup.accountStatus')}
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchStripeStatus}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {!stripeStatus?.has_account ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-8 h-8 text-amber-600" />
                </div>
                <h3 className="text-lg font-semibold mb-2">{t('admin.stripe.setup.accountRequired')}</h3>
                <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                  {t('admin.stripe.setup.accountRequiredDesc')}
                </p>
                
                {/* Error message display */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg text-left max-w-md mx-auto">
                    <p className="text-sm text-red-700 dark:text-red-300 font-medium">
                      {error}
                    </p>
                    {error.includes('Connect') && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                        {t('admin.stripe.setup.contactAdmin')}
                      </p>
                    )}
                  </div>
                )}
                
                <Button 
                  onClick={createStripeAccount} 
                  disabled={actionLoading}
                  className="btn-primary"
                  data-testid="create-stripe-account-btn"
                >
                  {actionLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CreditCard className="w-4 h-4 mr-2" />
                  )}
                  {t('admin.stripe.setup.createAccount')}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Status Items */}
                <div className="grid gap-3">
                  <StatusItem 
                    label={t('admin.stripe.setup.accountCreated')} 
                    status={stripeStatus.has_account} 
                  />
                  <StatusItem 
                    label={t('admin.stripe.setup.onboardingComplete')} 
                    status={stripeStatus.onboarding_complete} 
                  />
                  <StatusItem 
                    label={t('admin.stripe.setup.paymentsEnabled')} 
                    status={stripeStatus.charges_enabled} 
                  />
                  <StatusItem 
                    label={t('admin.stripe.setup.payoutsEnabled')} 
                    status={stripeStatus.payouts_enabled} 
                  />
                </div>

                {/* Requirements */}
                {stripeStatus.requirements?.length > 0 && (
                  <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-2">
                      {t('admin.stripe.setup.pendingVerification')}:
                    </h4>
                    <ul className="list-disc list-inside text-sm text-amber-700 dark:text-amber-300">
                      {stripeStatus.requirements.map((req, i) => (
                        <li key={i}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div className="flex flex-wrap gap-3 mt-6">
                  {needsOnboarding && (
                    <Button 
                      onClick={startOnboarding} 
                      disabled={actionLoading}
                      className="btn-primary"
                      data-testid="continue-onboarding-btn"
                    >
                      {actionLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ExternalLink className="w-4 h-4 mr-2" />
                      )}
                      {t('admin.stripe.setup.completeOnboarding')}
                    </Button>
                  )}
                  
                  {isFullySetup && (
                    <Button 
                      onClick={openStripeDashboard} 
                      disabled={actionLoading}
                      variant="outline"
                      data-testid="open-stripe-dashboard-btn"
                    >
                      {actionLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Banknote className="w-4 h-4 mr-2" />
                      )}
                      {t('admin.stripe.setup.openDashboard')}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{t('admin.stripe.setup.howItWorks')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                <span className="font-bold text-accent">1</span>
              </div>
              <div>
                <p className="font-medium text-foreground">{t('admin.stripe.setup.step1')}</p>
                <p>{t('admin.stripe.setup.step1Desc')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                <span className="font-bold text-accent">2</span>
              </div>
              <div>
                <p className="font-medium text-foreground">{t('admin.stripe.setup.step2')}</p>
                <p>{t('admin.stripe.setup.step2Desc')}</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                <span className="font-bold text-accent">3</span>
              </div>
              <div>
                <p className="font-medium text-foreground">{t('admin.stripe.setup.step3')}</p>
                <p>{t('admin.stripe.setup.step3Desc')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Success Message */}
        {isFullySetup && (
          <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-green-800 dark:text-green-200">
                {t('admin.stripe.setup.allSet')}
              </p>
              <p className="text-sm text-green-700 dark:text-green-300">
                {t('admin.stripe.setup.allSetDesc')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatusItem({ label, status }) {
  return (
    <div className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
      <span className="font-medium">{label}</span>
      {status ? (
        <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
          <CheckCircle className="w-3 h-3 mr-1" />
          Complété
        </Badge>
      ) : (
        <Badge variant="secondary">
          <AlertCircle className="w-3 h-3 mr-1" />
          En attente
        </Badge>
      )}
    </div>
  );
}
